import { buttonClasses, Button as MuiButton } from '@mui/material';
import type { ButtonProps as MuiButtonProps } from '@mui/material';
import { colors } from 'styles/theme/colors';
import styled from '@emotion/styled';
import type { Theme } from '@mui/material/styles';
import { forwardRef } from 'react';
import { type TooltipProps, Tooltip } from 'components/tooltip/Tooltip';

type BaseButtonProps = MuiButtonProps & {
  tooltipTitle?: React.ReactNode;
  tooltipProps?: Omit<TooltipProps, 'title'>;
};

type LinkButton = BaseButtonProps & {
  variant?: 'link' | 'linkBlack' | 'linkWhite';
  size?: 'lg' | 'md' | 'sm' | 'xl';
};

type OthersButton = BaseButtonProps & {
  size?: 'lg' | 'md' | 'sm';
};

export type ButtonProps = LinkButton | OthersButton;

/**
 * Button component, part of Delogue design system.
 * @param onClick - on click handler
 * @param variant - variant of the button, can be primary, secondary, tertiary, tertiaryOutline, quaternary, link, linkBlack, or linkWhite
 * @param size - size of the button, can be lg, md, sm, or xl
 * @param children - button content, usually plain text
 * @param startIcon - icon to be displayed at the start of the button
 * @param endIcon - icon to be displayed at the end of the button
 * @param tooltipTitle - Tooltip content shown on hover.
 * @param tooltipProps - Additional properties passed to the tooltip component.
 */

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ children, tooltipTitle, tooltipProps, ...rest }, ref) => {
    const buttonComponent = (
      <StyledButton ref={ref} {...rest}>
        {children}
      </StyledButton>
    );

    return tooltipTitle ? (
      <Tooltip title={tooltipTitle} {...tooltipProps}>
        <span>{buttonComponent}</span>
      </Tooltip>
    ) : (
      buttonComponent
    );
  }
);

Button.displayName = 'Button';

const StyledButton = styled(MuiButton)(({ theme }) => {
  return {
    height: 'fit-content',
    [`& .${buttonClasses.startIcon}`]: {
      marginRight: 'unset',
      marginLeft: 'unset',
      fill: 'currentcolor',
    },
    [`& .${buttonClasses.endIcon}`]: {
      marginRight: 'unset',
      marginLeft: 'unset',
      fill: 'currentcolor',
    },
  };
});

// Define the styles here and import them in styles/theme
// and use as basic styles for the themed button

export const primary = ({ theme }: { theme: Theme }) => ({
  borderRadius: '360px',
  color: theme.palette.text.primaryOnBrand,
  backgroundColor: theme.palette.bg.brandSolid,
  borderColor: theme.palette.bg.brandSolid,
  '&:hover': {
    backgroundColor: theme.palette.bg.brandSolidHover,
    borderColor: theme.palette.bg.brandSolidHover,
  },
  '&:active': {
    boxShadow: '0px 0px 0px 4px rgba(22, 105, 99, 0.08)',
  },
  '&:disabled': {
    backgroundColor: theme.palette.bg.disabledSubtle,
    borderColor: theme.palette.bg.disabledSubtle,
    color: theme.palette.text.primaryOnBrand,
  },
});

export const secondary = ({ theme }: { theme: Theme }) => ({
  borderRadius: '360px',
  color: theme.palette.text.brandSecondary,
  backgroundColor: theme.palette.bg.primary,
  borderColor: theme.palette.border.brand,
  '&:hover': {
    backgroundColor: theme.palette.bg.primary,
  },
  '&:active': {
    boxShadow: '0px 0px 0px 4px rgba(18, 84, 79, 0.08)',
  },
  '&:disabled': {
    color: theme.palette.text.disabledSubtle,
    backgroundColor: theme.palette.bg.primary,
    borderColor: theme.palette.border.disabledSubtle,
  },
});

export const tertiary = ({ theme }: { theme: Theme }) => ({
  color: theme.palette.text.primary,
  '&:hover': {
    backgroundColor: theme.palette.bg.brandPrimary,
    color: theme.palette.text.brandSecondary,
  },
  '&:active': {
    backgroundColor: theme.palette.bg.brandPrimary,
    color: theme.palette.text.brandSecondary,
  },
  '&:disabled': {
    color: theme.palette.text.disabledSubtle,
  },
});

export const tertiaryOutline = ({ theme }: { theme: Theme }) => ({
  color: theme.palette.text.secondary,
  borderColor: theme.palette.border.primarySubtle,
  backgroundColor: theme.palette.bg.primary,
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: theme.palette.utility.grey[50],
  },
  '&:active': {
    backgroundColor: theme.palette.bg.primary,
    boxShadow: '0px 0px 0px 4px rgba(69, 135, 130, 0.08)',
  },
  '&:disabled': {
    color: theme.palette.text.disabledSubtle,
  },
});

export const quaternary = ({ theme }: { theme: Theme }) => ({
  borderRadius: '8px',
  color: theme.palette.text.primaryOnBrand,
  backgroundColor: theme.palette.fg.infoPrimary,
  borderColor: theme.palette.fg.infoPrimary,
  '&:hover': {
    backgroundColor: theme.palette.bg.infoSecondary,
    borderColor: theme.palette.bg.infoSecondary,
  },
  '&:active': {
    backgroundColor: theme.palette.bg.infoSolid,
    borderColor: theme.palette.bg.infoSolid,
    boxShadow: '0px 0px 0px 4px rgba(81, 104, 255, 0.08)',
  },
  '&:disabled': {
    color: theme.palette.text.disabledSubtle,
    backgroundColor: theme.palette.bg.disabledSubtle,
    borderColor: theme.palette.bg.disabledSubtle,
  },
});

// links are not completed by product yet
export const linkButton = {
  color: colors.marineBlue[500],
  minWidth: 'max-content',
  gap: '4px',
  padding: 0,
  '&:hover': {
    color: colors.marineBlue[600],
    background: 'transparent',
    padding: 0,
  },
  '&:active': {
    color: colors.marineBlue[600],
    background: 'transparent',
    padding: 0,
  },
  '&:disabled': {
    color: colors.grey[200],
  },
};

export const linkBlack = {
  color: colors.grey[700],
  minWidth: 'max-content',
  gap: '4px',
  padding: 0,
  fontWeight: 600,
  '&:hover': {
    color: colors.marineBlue[500],
    background: 'transparent',
    padding: 0,
  },
  '&:active': {
    color: colors.marineBlue[500],
    background: 'transparent',
    padding: 0,
  },
  '&:disabled': {
    opacity: 0.25,
    color: colors.grey[700],
  },
};

export const linkWhite = {
  color: colors.white,
  minWidth: 'max-content',
  gap: '4px',
  padding: 0,
  fontWeight: 600,
  '&:hover': {
    color: colors.marineBlue[500],
    background: 'transparent',
    padding: 0,
  },
  '&:active': {
    color: colors.marineBlue[500],
    background: 'transparent',
    padding: 0,
  },
  '&:disabled': {
    opacity: 0.25,
    color: colors.white,
  },
};

export const lgButton = {
  display: 'inline-flex',
  padding: '9px 16px',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  fontSize: '1rem',
  lineHeight: '24px',
  border: '1px solid transparent',
};

export const mdButton = {
  display: 'inline-flex',
  padding: '9px 14px',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  fontSize: '0.875rem',
  lineHeight: '20px',
  border: '1px solid transparent',
};

export const smButton = {
  display: 'inline-flex',
  padding: '7px 12px',
  justifyContent: 'center',
  alignItems: 'center',
  flexShrink: 0,
  fontSize: '0.875rem',
  lineHeight: '20px',
  border: '1px solid transparent',
};

export const xlLinkButton = {
  padding: 0,
  alignItems: 'center',
  fontSize: '1.5rem',
  lineHeight: '32px',
};

export const lgLinkButton = {
  padding: 0,
  alignItems: 'center',
  fontSize: '1rem',
  lineHeight: '24px',
};

export const mdLinkButton = {
  padding: 0,
  alignItems: 'center',
  fontSize: '0.875rem',
  lineHeight: '20px',
};

export const smLinkButton = {
  padding: 0,
  alignItems: 'center',
  fontSize: '0.875rem',
  lineHeight: '20px',
};

export const BaseButton = styled.button<{ left?: boolean; right?: boolean }>`
  all: unset;
  cursor: pointer;
  display: flex;
  align-items: center;
  flex-direction: row;
  gap: 8px;
  font-size: 0.875rem;
  color: ${p => p.theme.palette.text.primary};
  margin-left: ${p => (p.left ? '16px' : 0)};
  margin-right: ${p => (p.right ? '16px' : 0)};
  &:disabled {
    cursor: not-allowed;
    color: ${p => p.theme.palette.text.disabledSubtle};
  }
`;
